import { useCallback, useState } from "react";

/**
 * useConfirmAction wraps an async action in a native confirm() prompt
 * and a busy/error state so the caller can render a button without
 * juggling confirm + try/catch + isPending manually.
 *
 * Use for one-shot destructive actions where a custom confirm dialog
 * is overkill (deleting a webhook, revoking a key). For richer flows
 * prefer the existing ConfirmDialog component.
 */
export function useConfirmAction<TArgs extends unknown[], TResult = void>(
  action: (...args: TArgs) => Promise<TResult>,
  message: string | ((...args: TArgs) => string),
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useCallback(
    async (...args: TArgs) => {
      const prompt = typeof message === "function" ? message(...args) : message;
      if (!window.confirm(prompt)) return;
      setBusy(true);
      setError(null);
      try {
        await action(...args);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
      } finally {
        setBusy(false);
      }
    },
    [action, message],
  );

  return { confirm, busy, error, clearError: () => setError(null) };
}
